import FindReplaceIcon from "@mui/icons-material/FindReplace";
import UploadIcon from "@mui/icons-material/Upload";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorData, getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import { useCreateDocumentsMutation, useGetDocumentsByIdsQuery } from "documents/_api/document-api";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";
import React, { useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router";
import {
  useCreatePolicyReplacementMutation,
  usePreviewPolicyReplacementQuery,
} from "work/policy-replacements/_api/policy-replacement-api";
import {
  CreatePolicyReplacementCommandJSON,
  PolicyReplacementAgreementJSON,
  PolicyReplacementCreatedJSON,
  PolicyReplacementCreatedJSONZ,
} from "work/policy-replacements/_schemas/policy-replacement-schema";
import PolicyReplacementAgreementAddition from "work/policy-replacements/_views/policy-replacement-agreement-addition";
import PolicyReplacementCreatedOutcome from "work/policy-replacements/_views/policy-replacement-created-outcome";
import PolicyReplacementPreviewGroups from "work/policy-replacements/_views/policy-replacement-preview-groups";
import PolicyReplacementPreview, {
  POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS,
} from "work/policy-replacements/policy-replacement-preview";
import {
  policyReplacementPath,
  proposalPath,
  ReplacePolicySearch,
  workAgreementPath,
} from "work/policy-replacements/policy-replacement-routes";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";

/** `PolicyReplacement.ReasonMaxLength` in the API. */
export const REASON_MAX_LENGTH = 1000;

// The page fills its area and scrolls inside it, so the action bar below can hold one place on
// screen. No bottom padding: the bar supplies its own, or it would sit that far above the edge at
// the end of the scroll and drop by as much when the form grew.
const PageContainer = styled(Paper)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: `calc(100% - ${theme.spacing(2)})`,
  margin: theme.spacing(1),
  overflow: "auto",
  padding: theme.spacing(2, 2, 0, 2),
}));
const Form = styled("div")(() => ({
  flex: "1 0 auto",
}));
const Section = styled("section")(({ theme }) => ({
  marginTop: theme.spacing(3),
  maxWidth: "56rem",
}));
const SectionExplanation = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));
const ChoiceRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const SelectorPanel = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  height: "28rem",
  marginTop: theme.spacing(1),
  overflow: "hidden",
  position: "relative",
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backdropFilter: "blur(5px) saturate(200%)",
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  height: "calc(100% - 2rem)",
  left: 0,
  margin: "1rem",
  position: "absolute",
  top: 0,
  width: "calc(100% - 2rem)",
  zIndex: 9999,
}));
// Pinned to the bottom of the page, so nothing that loads or changes above it can move the submit
// button. A click whose mouse-up lands off a button that moved since mouse-down is no click at all:
// the browser sends it to an ancestor and the button's handler never runs (#6653, #6748).
const ActionBar = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  bottom: 0,
  marginTop: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  position: "sticky",
  zIndex: 1,
}));
const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

/** What the page carries to the progress page, so it can say which selected agreements were left out. */
export type PolicyReplacementCreatedState = {
  created: PolicyReplacementCreatedJSON;
  agreementNames: Record<string, string>;
};

type ReplacementSubmission =
  | { outcome: "created"; created: PolicyReplacementCreatedJSON }
  | { outcome: "nothing-created"; created: PolicyReplacementCreatedJSON }
  | { outcome: "failed"; message: string };

type CreateReplacement = ReturnType<typeof useCreatePolicyReplacementMutation>[0];
type CreateDocuments = ReturnType<typeof useCreateDocumentsMutation>[0];

/**
 * Sends the create command and reads the answer. At module scope so the component keeps its
 * memoization: the React Compiler gives up on a component with control flow like this in a `try`.
 */
export async function submitPolicyReplacement(
  create: CreateReplacement,
  command: CreatePolicyReplacementCommandJSON,
): Promise<ReplacementSubmission> {
  let error: unknown;
  try {
    const created = await create(command).unwrap();
    return { outcome: "created", created };
  } catch (caught) {
    error = caught;
  }
  const status = getApiErrorStatus(error);
  if (status === HttpStatusCode.UnprocessableEntity) {
    const body = PolicyReplacementCreatedJSONZ.safeParse(getApiErrorData(error));
    if (body.success) return { outcome: "nothing-created", created: body.data };
  }
  if (status === HttpStatusCode.Forbidden) {
    return { outcome: "failed", message: "You may not share the new policy document, so it cannot replace this one." };
  }
  if (status === HttpStatusCode.NotFound) {
    return { outcome: "failed", message: "The new policy document no longer exists. Choose another." };
  }
  return { outcome: "failed", message: getErrorMessageFromUnknown(error) ?? "The policy could not be replaced. Try again." };
}

/** Uploads the new policy, answering the document it became or nothing, after telling the reader how it went. */
export async function uploadReplacementPolicy(createDocuments: CreateDocuments, file: File): Promise<Document | null> {
  const requests = createBulkDocumentRequests([file], { topics: [DocumentTopic.create({ context: "Policy" })] });
  let summary;
  try {
    summary = summarizeDocumentUploadResults(await createDocuments({ requests }).unwrap(), requests);
  } catch (error) {
    enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "The policy could not be uploaded. Try again.", {
      variant: "error",
    });
    return null;
  }
  const snackbar = getDocumentUploadSnackbar(summary);
  enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
  return summary.documents[0] ?? null;
}

/**
 * Replace one client policy across the agreements that hold it (#6257).
 *
 * The client chooses the new document, says why (the vendor reads it), and confirms the agreements
 * the preview found, clearing any it does not want and adding by hand any that holds a copy under
 * another document. Confirming records the replacement; a worker then opens one revision per
 * agreement, and the progress page follows them. See
 * `docs/design/proposals-negotiation/POLICY_REPLACEMENT_NEGOTIATION_DESIGN.md`.
 */
export default function ReplacePolicyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const replacedDocumentId = searchParams.get(ReplacePolicySearch.documentId) ?? "";
  const fromWorkAgreementId = searchParams.get(ReplacePolicySearch.workAgreementId) ?? undefined;

  const [replacement, setReplacement] = useState<Document | null>(null);
  const [reason, setReason] = useState("");
  const [additions, setAdditions] = useState<PolicyReplacementAgreementJSON[]>([]);
  // Every row the replacement can reach starts selected, up to the most agreements one replacement
  // may reach, so the page keeps only the rows the client changed: a row an added agreement brings in
  // arrives as the preview's default selection says.
  const [choices, setChoices] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [isChoosingExisting, setIsChoosingExisting] = useState(false);
  const [templateBeingFilled, setTemplateBeingFilled] = useState<Guid | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [nothingCreated, setNothingCreated] = useState<PolicyReplacementCreatedJSON | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [createDocuments] = useCreateDocumentsMutation();
  const [createReplacement] = useCreatePolicyReplacementMutation();
  const replacedDocumentQuery = useGetDocumentsByIdsQuery(
    replacedDocumentId ? { documentIds: [replacedDocumentId] } : skipToken,
  );
  const previewQuery = usePreviewPolicyReplacementQuery(
    replacedDocumentId ? { replacedDocumentId, additions } : skipToken,
  );
  const preview = useMemo(() => PolicyReplacementPreview.fromJSON(previewQuery.data), [previewQuery.data]);

  const replacedDocument = replacedDocumentQuery.data?.find((document) => document instanceof Document);
  const defaultSelection = preview?.defaultSelection ?? new Set<string>();
  const selection = new Set(
    (preview?.agreements ?? [])
      .map(PolicyReplacementPreview.rowKey)
      .filter((key) => choices.get(key) ?? defaultSelection.has(key)),
  );
  const agreements = preview?.agreementsToReplace(selection) ?? [];
  const agreementsOverLimit = agreements.length - POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS;
  const trimmedReason = reason.trim();
  const replacementIsTheOldPolicy = replacement?.id.value === replacedDocumentId;
  const canSubmit =
    Boolean(replacement) &&
    !replacementIsTheOldPolicy &&
    trimmedReason.length > 0 &&
    reason.length <= REASON_MAX_LENGTH &&
    agreements.length > 0 &&
    agreementsOverLimit <= 0 &&
    !isSubmitting;
  const previewedWorkAgreementIds = new Set((preview?.agreements ?? []).map((row) => row.workAgreementId));

  function toggleRow(rowKey: string) {
    const isSelected = selection.has(rowKey);
    setChoices((current) => new Map(current).set(rowKey, !isSelected));
  }

  function addAgreement(addition: PolicyReplacementAgreementJSON) {
    setAdditions((current) => [...current, addition]);
  }

  async function handleFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    setIsUploading(true);
    const uploaded = await uploadReplacementPolicy(createDocuments, file);
    setIsUploading(false);
    if (uploaded) {
      setReplacement(uploaded);
      setIsChoosingExisting(false);
    }
  }

  function handleExistingDocumentChosen(selected: Document[], deselected: Document[]) {
    const chosen = selected.at(-1);
    if (chosen) {
      setReplacement(chosen);
      return;
    }
    if (replacement && deselected.some((document) => document.id.isEqualTo(replacement.id))) setReplacement(null);
  }

  // Reached only through the button, which canSubmit enables only once both are in hand.
  async function handleSubmit(chosen: Document, confirmed: PolicyReplacementPreview) {
    setIsSubmitting(true);
    setFailure(null);
    setNothingCreated(null);
    const submission = await submitPolicyReplacement(createReplacement, {
      replacedDocumentId,
      replacementDocumentId: chosen.id.value,
      reason: trimmedReason,
      agreements,
    });
    setIsSubmitting(false);
    if (submission.outcome === "failed") {
      setFailure(submission.message);
      return;
    }
    if (submission.outcome === "nothing-created" || !submission.created.id) {
      setNothingCreated(submission.created);
      return;
    }
    const state: PolicyReplacementCreatedState = {
      created: submission.created,
      agreementNames: Object.fromEntries(confirmed.agreements.map((row) => [row.workAgreementId, row.name])),
    };
    navigate(policyReplacementPath(submission.created.id), { state });
  }

  if (!replacedDocumentId) {
    return (
      <PageContainer>
        <Alert severity="error">Choose the policy to replace from an agreement&apos;s Policies tab.</Alert>
      </PageContainer>
    );
  }

  const agreementNames = Object.fromEntries((preview?.agreements ?? []).map((row) => [row.workAgreementId, row.name]));

  return (
    <PageContainer>
      <Form>
        <Typography variant="h5" component="h1">
          Replace a policy across agreements
        </Typography>
        <SectionExplanation variant="body2">
          Each agreement you keep below gets a revision that replaces the policy. Its vendor accepts or
          rejects that revision like any other, and the old policy governs until the revision is hired.
        </SectionExplanation>
        {fromWorkAgreementId && (
          <Link component={RouterLink} to={workAgreementPath(fromWorkAgreementId)}>
            Back to the agreement
          </Link>
        )}

        <Section aria-labelledby="policy-being-replaced">
          <Typography id="policy-being-replaced" variant="h6" component="h2">
            Policy being replaced
          </Typography>
          <Typography variant="body1" data-testid="replaced-policy-name">
            {replacedDocument?.name.value ?? (replacedDocumentQuery.isLoading ? "Loading..." : "A client policy")}
          </Typography>
        </Section>

        <Section aria-labelledby="new-policy">
          <Typography id="new-policy" variant="h6" component="h2">
            New policy
          </Typography>
          <ChoiceRow>
            {replacement && (
              <Typography variant="body1" data-testid="replacement-policy-name">
                {replacement.name.value}
              </Typography>
            )}
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              loading={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {replacement ? "Upload another" : "Upload the new policy"}
            </Button>
            <Button variant="text" onClick={() => setIsChoosingExisting((open) => !open)}>
              {isChoosingExisting ? "Close your documents" : "Choose from your documents"}
            </Button>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              aria-label="New policy file"
              data-testid="replacement-policy-file"
              onChange={handleFileChosen}
            />
          </ChoiceRow>
          {replacementIsTheOldPolicy && (
            <Alert severity="warning" role="status">The new policy must be a different document from the one it replaces.</Alert>
          )}
          {isChoosingExisting && (
            <SelectorPanel>
              <DocumentSelector
                documentType="policy"
                lists="proposal-documents"
                selectedDocumentIds={replacement ? [replacement.id] : []}
                onDocumentSelectionChanged={handleExistingDocumentChosen}
                onCreateDocumentFromTemplate={(templateId) => setTemplateBeingFilled(templateId)}
              />
              {templateBeingFilled && (
                <Uploader
                  documentType={WorkDocumentType.ClientPolicy}
                  templateId={templateBeingFilled}
                  onDocumentUploaded={(document) => {
                    setReplacement(document);
                    setTemplateBeingFilled(null);
                    setIsChoosingExisting(false);
                  }}
                  onClose={() => setTemplateBeingFilled(null)}
                />
              )}
            </SelectorPanel>
          )}
        </Section>

        <Section aria-labelledby="replacement-reason">
          <Typography id="replacement-reason" variant="h6" component="h2">
            Reason
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Why the policy changes"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={reason.length > REASON_MAX_LENGTH}
            helperText={`The vendor reads this in the notice about each revision. ${reason.length}/${REASON_MAX_LENGTH}`}
          />
        </Section>

        <Section aria-labelledby="replacement-agreements">
          <Typography id="replacement-agreements" variant="h6" component="h2">
            Agreements
          </Typography>
          {previewQuery.isLoading && <Loader text="Finding the agreements that hold this policy..." />}
          {previewQuery.isError && (
            <Alert severity="error">
              {getErrorMessageFromUnknown(previewQuery.error) ?? "The agreements holding this policy could not be found."}
            </Alert>
          )}
          {preview && (
            <PolicyReplacementPreviewGroups
              preview={preview}
              selection={selection}
              disabled={isSubmitting}
              onToggle={toggleRow}
            />
          )}
          {preview && preview.reachableAgreementCount > POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS && (
            <Alert severity="info" role="status" sx={{ marginTop: 2 }}>
              This policy is on {preview.reachableAgreementCount} agreements a replacement can reach, and one
              replacement reaches at most {POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS}. The first{" "}
              {POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS} are selected; replace the policy on the rest with another
              replacement.
            </Alert>
          )}
          {preview && preview.unavailable.length > 0 && (
            <Alert severity="warning" role="status" sx={{ marginTop: 2 }}>
              {preview.unavailable.length === 1 ? "An agreement you added" : `${preview.unavailable.length} agreements you added`}{" "}
              could not be checked: {preview.unavailable.map((entry) => (entry.reason === "missing" ? "it no longer exists" : "you may not see its policies")).join("; ")}.
            </Alert>
          )}
          <Typography variant="subtitle1" component="h3" sx={{ marginTop: 2 }}>
            Add an agreement by hand
          </Typography>
          <SectionExplanation variant="body2">
            An agreement can hold a copy of this policy under another document. Choose the agreement and
            the policy on it that the new document replaces.
          </SectionExplanation>
          <PolicyReplacementAgreementAddition
            excludedWorkAgreementIds={previewedWorkAgreementIds}
            onAdd={addAgreement}
          />
        </Section>

        {preview && preview.firstNegotiations.length > 0 && (
          <Section aria-labelledby="first-negotiations">
            <Typography id="first-negotiations" variant="h6" component="h2">
              Proposals still in their first negotiation
            </Typography>
            <SectionExplanation variant="body2">
              These proposals carry the policy but are not hired yet. A replacement does not reach into
              them; change the policy in each negotiation.
            </SectionExplanation>
            <List dense disablePadding>
              {preview.firstNegotiations.map((negotiation) => (
                <ListItem key={negotiation.proposalId} disableGutters>
                  <ListItemText
                    primary={
                      <Link component={RouterLink} to={proposalPath(negotiation.proposalId)}>
                        {negotiation.name}
                      </Link>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Section>
        )}

      </Form>
      <ActionBar>
        {failure && (
          <Alert severity="error" role="alert" sx={{ marginTop: 2 }}>
            {failure}
          </Alert>
        )}
        {agreementsOverLimit > 0 && (
          <Alert severity="warning" role="status" sx={{ marginTop: 2 }}>
            One replacement reaches at most {POLICY_REPLACEMENT_MAXIMUM_AGREEMENTS} agreements, and{" "}
            {agreements.length} are selected. Clear {agreementsOverLimit} to continue.
          </Alert>
        )}
        {nothingCreated && <PolicyReplacementCreatedOutcome created={nothingCreated} agreementNames={agreementNames} />}
        <Actions>
          <Button
            variant="contained"
            startIcon={<FindReplaceIcon />}
            disabled={!canSubmit}
            loading={isSubmitting}
            onClick={() => handleSubmit(replacement!, preview!)}
          >
            Replace on {agreements.length} {agreements.length === 1 ? "agreement" : "agreements"}
          </Button>
        </Actions>
      </ActionBar>
    </PageContainer>
  );
}
